/**
 * CallOverlay - mock video call with the author.
 *
 * Lifecycle (state machine):
 *   dialing  -> ringing for a random 4-15s
 *   connecting -> author video preloads behind a poster
 *   live     -> author video plays, call timer counts up
 *   (end)    -> onEnd(durationSec) so the chat can log it + maybe follow up
 *
 * Controls: mic toggle (mute), cam toggle (drives the self picture-in-picture
 * preview via getUserMedia), and hang up. Camera/mic are best-effort: if the
 * user denies permission we simply show a placeholder PiP.
 */
import { useState, useEffect, useRef, useCallback } from 'preact/hooks';
import { AuthorAvatar } from './AuthorAvatar.jsx';
import { IconMic, IconMicOff, IconVideo, IconVideoOff, IconPhoneOff } from './icons.jsx';
import { randomCallVideo, CALL_PRELOADER } from '../lib/authorMedia.js';

const DIAL_MIN_MS = 4000;
const DIAL_MAX_MS = 15000;

const fmt = (sec) => {
  const m = Math.floor(sec / 60);
  return `${m}:${String(sec % 60).padStart(2, '0')}`;
};

export function CallOverlay({ chat, onEnd }) {
  const [phase, setPhase] = useState('dialing');
  const [seconds, setSeconds] = useState(0);
  const [micOn, setMicOn] = useState(true);
  const [camOn, setCamOn] = useState(false);

  const authorVideoRef = useRef(null);
  const selfVideoRef = useRef(null);
  const streamRef = useRef(null);
  const callVideo = useRef(randomCallVideo());
  const startedAt = useRef(0);

  // Ringing -> connecting after a ragged 4-15s.
  useEffect(() => {
    const dial = DIAL_MIN_MS + Math.random() * (DIAL_MAX_MS - DIAL_MIN_MS);
    const t = setTimeout(() => setPhase('connecting'), dial);
    return () => clearTimeout(t);
  }, []);

  // Connecting -> live once the author video can play (or after a short grace).
  useEffect(() => {
    if (phase !== 'connecting') return undefined;
    const goLive = () => {
      startedAt.current = Date.now();
      setPhase('live');
    };
    const v = authorVideoRef.current;
    if (!callVideo.current || !v) {
      const t = setTimeout(goLive, 1200);
      return () => clearTimeout(t);
    }
    const onReady = () => goLive();
    v.addEventListener('canplay', onReady, { once: true });
    const fallback = setTimeout(goLive, 4000);
    return () => {
      v.removeEventListener('canplay', onReady);
      clearTimeout(fallback);
    };
  }, [phase]);

  // Call timer while live.
  useEffect(() => {
    if (phase !== 'live') return undefined;
    const id = setInterval(() => {
      setSeconds(Math.round((Date.now() - startedAt.current) / 1000));
    }, 1000);
    return () => clearInterval(id);
  }, [phase]);

  // Author video play/pause follows phase.
  useEffect(() => {
    const v = authorVideoRef.current;
    if (v && phase === 'live') v.play().catch(() => {});
  }, [phase]);

  // Self camera stream follows the cam toggle.
  useEffect(() => {
    let cancelled = false;
    const stop = () => {
      if (streamRef.current) {
        streamRef.current.getTracks().forEach((t) => t.stop());
        streamRef.current = null;
      }
      if (selfVideoRef.current) selfVideoRef.current.srcObject = null;
    };
    if (camOn && navigator.mediaDevices?.getUserMedia) {
      navigator.mediaDevices
        .getUserMedia({ video: true, audio: false })
        .then((stream) => {
          if (cancelled) {
            stream.getTracks().forEach((t) => t.stop());
            return;
          }
          streamRef.current = stream;
          if (selfVideoRef.current) {
            selfVideoRef.current.srcObject = stream;
            selfVideoRef.current.play().catch(() => {});
          }
        })
        .catch(() => setCamOn(false));
    } else {
      stop();
    }
    return () => {
      cancelled = true;
      stop();
    };
  }, [camOn]);

  const hangUp = useCallback(() => {
    const dur = phase === 'live' ? seconds : 0;
    onEnd?.(dur);
  }, [phase, seconds, onEnd]);

  const status =
    phase === 'dialing'
      ? 'Calling…'
      : phase === 'connecting'
        ? 'Connecting…'
        : fmt(seconds);

  return (
    <div class="call">
      <div class="call__stage">
        {callVideo.current && (
          <video
            ref={authorVideoRef}
            class={`call__author ${phase === 'live' ? 'is-live' : ''}`}
            src={callVideo.current}
            poster={CALL_PRELOADER}
            playsinline
            loop
          />
        )}
        {phase !== 'live' && (
          <div class="call__poster" style={{ backgroundImage: `url(${CALL_PRELOADER})` }} />
        )}

        {phase !== 'live' ? (
          <div class="call__topinfo">
            <AuthorAvatar letter={chat.letter} seed={chat.id} size={84} />
            <span class="call__name">{chat.authorName}</span>
            <span class="call__status">{status}</span>
          </div>
        ) : (
          <div class="call__topinfo call__topinfo--live">
            <AuthorAvatar letter={chat.letter} seed={chat.id} size={44} />
            <div class="call__topinfo-meta">
              <span class="call__name">{chat.authorName}</span>
              <span class="call__status">{status}</span>
            </div>
          </div>
        )}

        {/* Self picture-in-picture */}
        <div class="call__pip">
          {camOn ? (
            <video ref={selfVideoRef} class="call__pipvideo" playsinline muted />
          ) : (
            <div class="call__pipoff">Camera off</div>
          )}
        </div>
      </div>

      <div class="call__controls">
        <button
          type="button"
          class={`call__btn ${micOn ? '' : 'is-off'}`}
          onClick={() => setMicOn((v) => !v)}
          aria-label={micOn ? 'Mute' : 'Unmute'}
        >
          {micOn ? <IconMic size={26} /> : <IconMicOff size={26} />}
        </button>
        <button type="button" class="call__btn call__btn--end" onClick={hangUp} aria-label="End call">
          <IconPhoneOff size={28} />
        </button>
        <button
          type="button"
          class={`call__btn ${camOn ? '' : 'is-off'}`}
          onClick={() => setCamOn((v) => !v)}
          aria-label={camOn ? 'Turn camera off' : 'Turn camera on'}
        >
          {camOn ? <IconVideo size={26} /> : <IconVideoOff size={26} />}
        </button>
      </div>
    </div>
  );
}
